<template>
  <div>
    <b-dropdown size="sm" variant="white" text="Import / Export" right>
      <b-dropdown-item-button @click="download('live')">
        Export Live Versions
      </b-dropdown-item-button>
      <b-dropdown-item-button @click="download('latest')">
        Export Latest Versions
      </b-dropdown-item-button>
      <b-dropdown-divider />
      <b-dropdown-item-button @click="openImport">
        Import Workbook
      </b-dropdown-item-button>
    </b-dropdown>

    <b-modal
      :id="modalId"
      title="Import translation workbook"
      size="lg"
      @hidden="reset"
      hide-footer
    >
      <b-alert v-if="error" variant="danger" show>{{ error }}</b-alert>
      <b-alert v-if="applied" variant="success" show>
        Imported {{ applied.applied_segments }} translated {{ appliedFieldWord }} across
        {{ applied.applied_translations }} translation {{ appliedRecordWord }}. All affected translations have been marked as incomplete.
      </b-alert>

      <p class="text-muted">
        This workbook must be from the <strong>{{ project.name }}</strong>
        for <strong>{{ language.name }}</strong>.
      </p>

      <b-form-file
        v-model="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        placeholder="Choose a .xlsx translation workbook"
        drop-placeholder="Drop workbook here"
        :disabled="working"
        @input="fileChanged"
      />

      <div class="mt-3" v-if="preview">
        <h6>Import preview</h6>
        <b-table-simple small bordered responsive>
          <b-thead>
            <b-tr>
              <b-th>New values</b-th>
              <b-th>Updated values</b-th>
              <b-th>Unchanged</b-th>
              <b-th>Blank</b-th>
              <b-th>Stale</b-th>
              <b-th>Invalid</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr>
              <b-td>{{ preview.counts.create }}</b-td>
              <b-td>{{ preview.counts.update }}</b-td>
              <b-td>{{ preview.counts.unchanged }}</b-td>
              <b-td>{{ preview.counts.blank }}</b-td>
              <b-td>{{ preview.counts.stale }}</b-td>
              <b-td>{{ preview.counts.invalid }}</b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>

        <b-alert v-if="preview.details.length" variant="warning" show>
          Stale and invalid rows will be skipped. Review the details below before importing.
        </b-alert>
        <b-table
          v-if="preview.details.length"
          :items="preview.details"
          :fields="detailFields"
          small
          bordered
          responsive
        />
        <small v-if="preview.details_truncated" class="text-muted">
          Only the first 200 row issues are shown.
        </small>
      </div>

      <div class="d-flex justify-content-end mt-4">
        <b-button variant="white" class="mr-2" @click="close" :disabled="working">
          Close
        </b-button>
        <b-button
          v-if="!preview"
          variant="primary"
          @click="previewImport"
          :disabled="!file || working"
        >
          <b-spinner small v-if="working" />
          Preview import
        </b-button>
        <b-button
          v-else
          variant="primary"
          @click="applyImport"
          :disabled="changeCount === 0 || working || !!applied"
        >
          <b-spinner small v-if="working" />
          Import {{ changeCount }} translated {{ changeFieldWord }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { HTTP } from '../../utils/requests'
import { SEI_API_BASE } from '../../utils/constants'
import { EVENT } from '../../utils/event-bus'

export default {
  name: 'TranslationWorkbook',
  props: {
    project: {
      type: Object,
      required: true
    },
    language: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      file: null,
      preview: null,
      applied: null,
      error: '',
      working: false,
      detailFields: [
        {key: 'row', label: 'Row'},
        {key: 'status', label: 'Status'},
        {key: 'message', label: 'Reason'}
      ]
    }
  },
  computed: {
    modalId() {
      return `translation-workbook-${this.language.id}`
    },
    changeCount() {
      if (!this.preview) return 0
      return this.preview.counts.create + this.preview.counts.update
    },
    changeFieldWord() {
      return this.changeCount === 1 ? 'field' : 'fields'
    },
    appliedFieldWord() {
      return this.applied && this.applied.applied_segments === 1 ? 'field' : 'fields'
    },
    appliedRecordWord() {
      return this.applied && this.applied.applied_translations === 1 ? 'record' : 'records'
    },
    workbookUrl() {
      return `${SEI_API_BASE}/exams/${this.project.id}/translations/workbook`
    }
  },
  methods: {
    async download(resourceVersions) {
      this.working = true
      try {
        const response = await HTTP.get(this.workbookUrl, {
          params: {
            language_id: this.language.id,
            resource_versions: resourceVersions
          },
          responseType: 'blob'
        })
        const disposition = response.headers['content-disposition'] || ''
        const filenameMatch = disposition.match(/filename="?([^";]+)"?/)
        const filename = filenameMatch ? filenameMatch[1] : 'scorpion-translations.xlsx'
        const url = window.URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        EVENT.alert({
          variant: 'danger',
          message: await this.errorMessage(error, 'Could not export the translation workbook.')
        })
      } finally {
        this.working = false
      }
    },
    openImport() {
      this.$bvModal.show(this.modalId)
    },
    close() {
      this.$bvModal.hide(this.modalId)
    },
    reset() {
      this.file = null
      this.preview = null
      this.applied = null
      this.error = ''
      this.working = false
    },
    fileChanged() {
      this.preview = null
      this.applied = null
      this.error = ''
    },
    formData() {
      const data = new FormData()
      data.append('language_id', this.language.id)
      data.append('file', this.file)
      return data
    },
    async previewImport() {
      this.working = true
      this.error = ''
      try {
        const response = await HTTP.post(`${this.workbookUrl}/preview`, this.formData())
        this.preview = response.data
      } catch (error) {
        this.error = await this.errorMessage(error, 'Could not preview the translation workbook.')
      } finally {
        this.working = false
      }
    },
    async applyImport() {
      this.working = true
      this.error = ''
      try {
        const response = await HTTP.post(`${this.workbookUrl}/apply`, this.formData())
        this.applied = response.data
        this.preview = response.data
        this.$emit('imported')
      } catch (error) {
        this.error = await this.errorMessage(error, 'Could not import the translation workbook.')
      } finally {
        this.working = false
      }
    },
    async errorMessage(error, fallback) {
      if (error.response && error.response.data instanceof Blob) {
        try {
          const payload = JSON.parse(await error.response.data.text())
          return payload.error || fallback
        } catch (parseError) {
          return fallback
        }
      }
      return error.response?.data?.error || fallback
    }
  }
}
</script>
